// ***************************************
// DialogBase
//
// This dialog contains the basic functionality of all dialogs
//
// Copyright 2015-2016 Tingly Games B.V.
// Last Changed: 11-1-2016
// ***************************************
import { Event } from "@cg-package/coolkit-utilities";
import { tgfSettings } from "../tgfSettings";
import { tbContainer } from "../../engine/tbContainer";
import { tgfGraphics } from "../tgfGraphics";
import { tbVector } from "../../engine/tbVector";
import { tbGame } from "../../engine/tbGame";
import { tgfFunctions } from "../tgfFunctions";
tgfSettings.dialogDefaults.base = {
    canvas: null,
    layer: -1000,
    depth: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    propagateCancelEvents: true,
    baseContainer: null
};
var DialogBase = /** @class */ (function () {
    function DialogBase(properties) {
        this.canvas = null;
        this.finishedShowing = new Event();
        // Initialize properties
        this.initialize(properties);
    }
    // Meant to be overriden
    DialogBase.prototype.doShow = function () { };
    DialogBase.prototype.doHide = function () { };
    DialogBase.prototype.doFree = function () { };
    DialogBase.prototype.doPrepare = function () { };
    DialogBase.prototype.doActivate = function () { };
    DialogBase.prototype.doDeactivate = function () { };
    DialogBase.prototype.doUpdateVisuals = function () { };
    // ***************************************
    // General methods (can be overridden)
    // ***************************************
    // Initialize the properties of the dialog
    DialogBase.prototype.initialize = function (properties) {
        properties = properties || {};
        this.setProperties(tgfSettings.dialogDefaults.base); // The base properties must always be included first
        this.setProperties(properties);
        this.baseContainer = new tbContainer(tgfGraphics.areaWidth, tgfGraphics.areaHeight);
        this.baseContainer.setPosition(this.x, this.y);
        this.baseContainer.name = this.name;
        this.parent = null; // Dialogs do not have parents (yet)
        this.parentDialog = properties.parentDialog; // Dialogs do not have parents (yet)
        this.kind = "dialog"; // The kind (must be "dialog")
        this._components = []; // The components in the dialog
        this.isPrepared = false; // Whether prepared (true, false)
        this.isVisible = false; // Whether visible (true, false)
        this.isActive = false; // Whether active (true, false)
        if (this.canvas === null) {
            this.canvas = tgfGraphics.mainCanvas;
        }
    };
    // Sets a number of properties of the dialog
    DialogBase.prototype.setProperties = function (properties) {
        var _a;
        var property;
        if (typeof properties !== "object") {
            return;
        }
        for (property in properties) {
            if (properties.hasOwnProperty(property)) {
                this[property] = tgfFunctions.copyValue(properties[property]);
            }
        }
        (_a = this.baseContainer) === null || _a === void 0 ? void 0 : _a.setPosition(this.x, this.y);
    };
    // Transform the screen (x,y)-coordinate to the component x-coordinate
    DialogBase.prototype.screenToComponentX = function (screenX, screenY) {
        if (this.parent !== null) {
            return this.parent.screenToComponentX(screenX, screenY) - this.x;
        }
        return screenX - this.x;
    };
    // Transform the screen (x,y)-coordinate to the component y-coordinate
    DialogBase.prototype.screenToComponentY = function (screenX, screenY) {
        if (this.parent !== null) {
            return this.parent.screenToComponentY(screenX, screenY) - this.y;
        }
        return screenY - this.y;
    };
    // Transform the screen (x,y)-coordinate to a component-space vector
    DialogBase.prototype.screenToComponentVector = function (screenX, screenY) {
        return new tbVector(this.screenToComponentX(screenX, screenY), this.screenToComponentY(screenX, screenY));
    };
    // Transform the component (x,y)-coordinate to the screen x-coordinate
    DialogBase.prototype.componentToScreenX = function (componentX, componentY) {
        if (this.parent !== null) {
            return this.parent.componentToScreenX(componentX + this.x, componentY + this.y);
        }
        return componentX + this.x;
    };
    // Transform the component (x,y)-coordinate to the screen y-coordinate
    DialogBase.prototype.componentToScreenY = function (componentX, componentY) {
        if (this.parent !== null) {
            return this.parent.componentToScreenY(componentX + this.x, componentY + this.y);
        }
        return componentY + this.y;
    };
    // Transform the component (x,y)-coordinate to a screen-space vector
    DialogBase.prototype.componentToScreenVector = function (componentX, componentY) {
        return new tbVector(this.componentToScreenX(componentX, componentY), this.componentToScreenY(componentX, componentY));
    };
    // Returns the x-position of the finger with respect to the component
    DialogBase.prototype.touchX = function (finger) {
        return this.screenToComponentX(tbGame.touchX(finger), tbGame.touchY(finger));
    };
    // Returns the y-position of the finger with respect to the component
    DialogBase.prototype.touchY = function (finger) {
        return this.screenToComponentY(tbGame.touchX(finger), tbGame.touchY(finger));
    };
    DialogBase.prototype.containerRelativeX = function () {
        return 0;
    };
    DialogBase.prototype.containerRelativeY = function () {
        return 0;
    };
    // ***************************************
    // Dealing with child components
    // ***************************************
    DialogBase.prototype.addChild = function (child) {
        this.baseContainer.addChild(child);
    };
    DialogBase.prototype.removeChild = function (child) {
        this.baseContainer.removeChild(child);
    };
    // Add a child component
    DialogBase.prototype.addComponent = function (component) {
        component.parent = this;
        this._components.push(component);
    };
    // Insert a child component
    DialogBase.prototype.insertComponent = function (index, component) {
        component.parent = this;
        if (index < 0) {
            index = 0;
        }
        if (index > this._components.length) {
            index = this._components.length;
        }
        this._components.splice(index, 0, component);
    };
    // Returns the component with the given name
    DialogBase.prototype.findComponent = function (name, recursive) {
        var result = null;
        if (recursive === undefined) {
            recursive = false;
        }
        for (var i = 0; i < this._components.length; i += 1) {
            if (this._components[i].name === name) {
                result = this._components[i];
            }
            if (recursive) {
                result = this._components[i].findComponent(name, recursive);
                if (result !== null) {
                    return result;
                }
            }
        }
        return result;
    };
    // Removes all components
    DialogBase.prototype.removeAllComponents = function () {
        this._components = [];
    };
    // Removes the component
    DialogBase.prototype.removeComponent = function (component, recursive) {
        if (recursive === void 0) { recursive = false; }
        for (var i = this._components.length - 1; i >= 0; i -= 1) {
            if (recursive) {
                this._components[i].removeComponent(component, recursive);
            }
            if (this._components[i] === component) {
                this._components.splice(i, 1);
            }
        }
    };
    // Removes the component with the given name
    DialogBase.prototype.removeComponentByName = function (name, recursive) {
        var i;
        if (recursive === undefined) {
            recursive = false;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (recursive) {
                this._components[i].removeComponent(name, recursive);
            }
            if (this._components[i].name === name) {
                this._components.splice(i, 1);
            }
        }
    };
    // Returns the components whose tags contain one or all of the values
    DialogBase.prototype.findComponentsByTags = function (tags, all, recursive) {
        var i, result = [], result2;
        if (all === undefined) {
            all = false;
        }
        if (recursive === undefined) {
            recursive = false;
        }
        for (i = 0; i < this._components.length; i += 1) {
            if (this._components[i].containsTags(tags, all)) {
                result.push(this._components[i]);
            }
            if (recursive) {
                result2 = this._components[i].findComponentsByTags(tags, all, recursive);
                result = result.concat(result2);
            }
        }
        return result;
    };
    // Removes the components whose tags contain one or all of the values
    DialogBase.prototype.removeComponentsByTags = function (tags, all, recursive) {
        var i;
        if (all === undefined) {
            all = false;
        }
        if (recursive === undefined) {
            recursive = false;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (recursive) {
                this._components[i].removeComponentsByTags(tags, all, recursive);
            }
            if (this._components[i].containsTags(tags, all)) {
                this._components.splice(i, 1);
            }
        }
    };
    // ***************************************
    // Preparing, freeing,showing and hiding
    // ***************************************
    // Prepare the dialog for use
    DialogBase.prototype.prepare = function (alsoChildren) {
        if (alsoChildren === void 0) { alsoChildren = true; }
        var i;
        this.baseContainer.hide();
        // Adding the baseContainer to the parentDialog or directly to the main canvas if there is no parent
        this.baseContainer.add(this.parentDialog);
        if (this.isPrepared) {
            return;
        }
        if (this.onPrepare) {
            if (this.onPrepare(this)) {
                return;
            }
        }
        this.isPrepared = true;
        if (this.doPrepare) {
            this.doPrepare();
        }
        if (alsoChildren) {
            for (i = 0; i < this._components.length; i += 1) {
                this._components[i].prepare();
            }
        }
    };
    // Fee the dialog from use
    DialogBase.prototype.free = function (alsoChildren) {
        if (alsoChildren === void 0) { alsoChildren = true; }
        if (!this.isPrepared) {
            return;
        }
        if (this.onFree) {
            if (this.onFree(this)) {
                return;
            }
        }
        if (this.isVisible) {
            this.hide();
        }
        this.isPrepared = false;
        if (alsoChildren) {
            for (var i = 0; i < this._components.length; i += 1) {
                this._components[i].free();
            }
        }
        if (this.doFree) {
            this.doFree();
        }
        if (this.baseContainer) {
            this.baseContainer.remove(this.parentDialog);
            this.baseContainer.free();
            this.baseContainer = null;
        }
    };
    // Show the dialog, making it visible
    DialogBase.prototype.show = function (alsoChildren) {
        var _a;
        if (alsoChildren === void 0) { alsoChildren = true; }
        if (this.canvas === null) {
            this.canvas = tgfGraphics.mainCanvas;
        }
        if (this.isVisible) {
            return;
        }
        if (!this.isPrepared) {
            this.prepare();
        }
        if (this.onShow) {
            if (this.onShow(this)) {
                return;
            }
        }
        this.isVisible = true;
        this.baseContainer.show();
        this.activate();
        // Show the components (the order matters such that the dialog can hide certain components later)
        if (alsoChildren) {
            for (var i = 0; i < this._components.length; i += 1) {
                this._components[i].show();
            }
        }
        this.doShow();
        (_a = this.finishedShowing) === null || _a === void 0 ? void 0 : _a.fire();
        // Add it to the game engine
        this.visible = true;
        this.active = true;
        tbGame.addToken(this);
    };
    // Hide the dialog, making it invisible
    DialogBase.prototype.hide = function (alsoChildren) {
        if (alsoChildren === void 0) { alsoChildren = true; }
        var i;
        if (!this.isVisible) {
            return;
        }
        if (this.onHide) {
            if (this.onHide(this)) {
                return;
            }
        }
        this.deactivate();
        this.isVisible = false;
        this.baseContainer.hide();
        // Hide the components (the order matters such that the dialog can show certain components later)
        if (alsoChildren) {
            for (i = 0; i < this._components.length; i += 1) {
                this._components[i].hide();
            }
        }
        if (this.doHide) {
            this.doHide();
        }
        // Remove it from the game engine
        this.visible = false;
        this.active = false;
        tbGame.removeToken(this);
    };
    // Activates the dialog
    DialogBase.prototype.activate = function (alsoChildren) {
        if (alsoChildren === void 0) { alsoChildren = true; }
        var i;
        if (this.isActive) {
            return;
        }
        if (this.onActivate) {
            if (this.onActivate(this)) {
                return;
            }
        }
        this.isActive = true;
        // Activate the components
        if (alsoChildren) {
            for (i = 0; i < this._components.length; i += 1) {
                this._components[i].activate();
            }
        }
        if (this.doActivate) {
            this.doActivate();
        }
    };
    // Deactivates the dialog
    DialogBase.prototype.deactivate = function (alsoChildren) {
        if (alsoChildren === void 0) { alsoChildren = true; }
        var i;
        if (!this.isActive) {
            return;
        }
        if (this.onDeactivate) {
            if (this.onDeactivate(this)) {
                return;
            }
        }
        this.isActive = false;
        // Deactivate the components
        if (alsoChildren) {
            for (i = 0; i < this._components.length; i += 1) {
                this._components[i].deactivate();
            }
        }
        if (this.doDeactivate) {
            this.doDeactivate();
        }
    };
    DialogBase.prototype.bringToFront = function () {
        var parent = this.baseContainer.container.parent;
        parent.removeChild(this.baseContainer.container);
        parent.addChild(this.baseContainer.container);
    };
    // ***************************************
    // Event handlers
    // ***************************************
    // handleAdd is called when the token is (re-) added to the game.
    DialogBase.prototype.handleAdd = function () {
    };
    // handleRemove is called when the token is removed from the game.
    DialogBase.prototype.handleRemove = function () {
    };
    // handleBeginStep is called each step before processing other events.
    DialogBase.prototype.handleBeginStep = function (timePassed) {
        var i;
        if (!this.isActive) {
            return;
        }
        for (i = 0; i < this._components.length; i += 1) {
            if (this._components[i].handleBeginStep(timePassed)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleTouchDown is called when a touch is started or if a mouse button is pressed.
    DialogBase.prototype.handleTouchDown = function (finger, x, y) {
        if (!this.isActive) {
            return;
        }
        for (var i = this._components.length - 1; i >= 0; i -= 1) {
            if (this._components[i].handleTouchDown(finger, x, y)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleTouchUp is called when a touch is ended or if a mouse button is released.
    DialogBase.prototype.handleTouchUp = function (finger, x, y) {
        if (!this.isActive) {
            return;
        }
        for (var i = this._components.length - 1; i >= 0; i -= 1) {
            if (this._components[i].handleTouchUp(finger, x, y)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleMouseWheel is called when the player changes the position of the mouse wheel.
    DialogBase.prototype.handleMouseWheel = function (delta, x, y) {
        var i;
        if (!this.isActive) {
            return;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (this._components[i].handleMouseWheel(delta, x, y)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleImmediateTouchDown is called immediately when a touch is started or if a mouse button is pressed.
    DialogBase.prototype.handleImmediateTouchDown = function (finger, x, y) {
        var i;
        if (!this.isActive) {
            return;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (this._components[i].handleImmediateTouchDown(finger, x, y)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleImmediateTouchUp is called immediately when a touch is ended or if a mouse button is released.
    DialogBase.prototype.handleImmediateTouchUp = function (finger, x, y) {
        var i;
        if (!this.isActive) {
            return;
        }
        for (i = this._components.length - 1; i >= 0; i -= 1) {
            if (this._components[i].handleImmediateTouchUp(finger, x, y)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleKeyDown is called if the key with the given keyCode is pressed.
    DialogBase.prototype.handleKeyDown = function (keyCode) {
        var i;
        if (!this.isActive) {
            return;
        }
        for (i = 0; i < this._components.length; i += 1) {
            if (this._components[i].handleKeyDown(keyCode)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleKeyUp is called if the key with the given keyCode is released.
    DialogBase.prototype.handleKeyUp = function (keyCode) {
        var i;
        if (!this.isActive) {
            return;
        }
        for (i = 0; i < this._components.length; i += 1) {
            if (this._components[i].handleKeyUp(keyCode)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleAlarm is called if a global alarm is reached.
    DialogBase.prototype.handleAlarm = function (alarm) {
        if (!this.isActive) {
            return;
        }
        for (var i = 0; i < this._components.length; i += 1) {
            if (this._components[i].handleAlarm(alarm)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleSpecial is called if a special or user-defined event happens.
    DialogBase.prototype.handleSpecial = function (id, args) {
        if (!this.isActive) {
            return;
        }
        for (var i = 0; i < this._components.length; i += 1) {
            if (this._components[i].handleSpecial(id, args)) {
                return this.propagateCancelEvents;
            }
        }
    };
    // handleEndStep is called each step after processing other events (before draw).
    DialogBase.prototype.handleEndStep = function (timePassed) {
        var i, result = false;
        if (this.isActive) {
            for (i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleEndStep(timePassed)) {
                    result = this.propagateCancelEvents;
                    break;
                }
            }
        }
        if (this.isVisible) {
            for (i = 0; i < this._components.length; i += 1) {
                if (this._components[i].handleUpdateVisuals(timePassed)) {
                    result = this.propagateCancelEvents;
                    break;
                }
            }
        }
        return result;
    };
    // handleDraw is called at the end of a step to draw the token.
    DialogBase.prototype.handleDraw = function () {
        var i, result = false;
        for (i = 0; i < this._components.length; i += 1) {
            if (this._components[i].handleDraw()) {
                result = this.propagateCancelEvents;
                break;
            }
        }
        return result;
    };
    return DialogBase;
}());
export { DialogBase };
